import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Row, Col, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import reportApi from "../../services/reportapi";
import branchApi from "../../services/branchapi";
import moment from "moment";
import Loader from "../loader/loader";
import DynamicBreadcrumb from "../header/breadcrumb";

const SalesProductReport = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [branches, setBranches] = useState([]);
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const formatDate = (date) => date.toISOString().slice(0, 10);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [filters, setFilters] = useState({
        fromDate: formatDate(firstDay),
        toDate: formatDate(lastDay),
        branchId: "",
        top: "",
    });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchData = await branchApi.getAllBranches({
                    limit: 100,
                    page: 0,
                    status: 1,
                    sortModel: [{ field: "branchName", sort: "asc" }],
                });
                const branchList = branchData.data.result || [];
                setBranches(branchList);
            } catch (error) {
                console.error("Error fetching branches:", error);
            }
        };
        fetchBranches();
    }, []);

    useEffect(() => {
        if (filters.branchId) {
            fetchProductReport();
        }
    }, [filters.branchId]);

    const updateFilters = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchProductReport = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const search = filterModel?.quickFilterValues?.[0] ?? "";
            const payload = {
                limit: pageSize,
                page,
                search,
                ...filters
            };

            const res = await reportApi.getSalesProductReport(payload);

            const formatted = res?.data?.rows?.map((item, index) => ({
                ...item,
                id: item.itemId,
                sno: index + 1 + page * pageSize
            })) || [];

            setRows(formatted);
            setTotalRecords(res?.data?.recordsTotal || 0);
        } catch (error) {
            console.error("Error fetching product report:", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, filters, filterModel]);

    useEffect(() => {
        fetchProductReport();
    }, [fetchProductReport]);


    const exportData = async () => {
        try {
            const payload = { ...filters, limit: 10000, page: 0 };
            const res = await reportApi.getSalesProductReport(payload);
            const allRows = res.data?.rows || [];

            const csvRows = [
                ["SNO", "Product", "Item Code", "Unit", "Qty", "Total"],
            ];

            allRows.forEach((row, i) => {
                csvRows.push([
                    i + 1,
                    row.productName,
                    row.refNo,
                    `${row.unit} ${row.unitType}`,
                    row.totalQty,
                    row.totalPrice,
                ]);
            });

            const blob = new Blob([csvRows.map((e) => e.join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", "sales_product_report.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Export error:", error);
        }
    };

    const columns = [
        { field: "sno", headerName: "S NO", width: 80 },
        { field: "productName", headerName: "Product Name", flex: 1 },
        { field: "refNo", headerName: "Item Code", flex: 1 },
        { field: "unit", headerName: "Unit", flex: 1 },
        { field: "unitType", headerName: "Type", flex: 1 },
        { field: "totalQty", headerName: "Total Quantity", flex: 1 },
        { field: "totalPrice", headerName: "Total Price", flex: 1 },
    ];

    const pathArray = [{ name: "Itemwise Sales Report", url: null }];
    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <h5 className="mb-3">Itemwise Sales Report</h5>

            {/* Filters */}
            <Form className="mb-3">
                <Row>
                    <Col md={2}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={filters.branchId}
                            onChange={(e) => updateFilters("branchId", e.target.value)}
                        >
                            <option value="">All</option>
                            {branches.map((b) => (
                                <option key={b.branchId} value={b.branchId}>{b.branchName}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.fromDate}
                            onChange={(e) => updateFilters("fromDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.toDate}
                            onChange={(e) => updateFilters("toDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Form.Label>Sort By</Form.Label>
                        <Form.Select
                            value={filters.top}
                            onChange={(e) => updateFilters("top", e.target.value)}
                        >
                            <option value="">Default</option>
                            <option value="qty">Top by Qty</option>
                            <option value="pri">Top by Price</option>
                            <option value="nameasc">Name Asc</option>
                            <option value="namedesc">Name Desc</option>
                            <option value="date">Date Asc</option>
                            <option value="datedesc">Date Desc</option>
                        </Form.Select>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                        <Button variant="primary" onClick={fetchProductReport}>
                            Filter
                        </Button>
                        <Button variant="success" className="ms-2" onClick={exportData}>
                            Export
                        </Button>
                    </Col>
                </Row>
            </Form>

            {loading && <Loader />}

            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    paginationMode="server"
                    rowCount={totalRecords}
                    pageSizeOptions={[5, 10, 15, 20, 50]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    loading={loading}
                    checkboxSelection
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                />
            </Box>
        </Container>
    );
};

export default SalesProductReport;
